import { useEffect, useState } from "react"
import { useLocation, useNavigate } from "react-router-dom"
import { QRCodeSVG } from "qrcode.react"
import { authenticationApi } from "@/lib/api"
import { authStore, type User } from "@/stores/authStore"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { Alert, AlertDescription } from "@/components/ui/alert"
import { LoadingSpinner } from "@/components/ui/loading-spinner"
import { CheckCircle, Shield } from "lucide-react"

type TwoFactorState = {
  challengeToken?: string
  setupRequired?: boolean
  email?: string
}

export default function TwoFactorPage() {
  const navigate = useNavigate()
  const location = useLocation()
  const state = (location.state ?? {}) as TwoFactorState
  const challengeToken = state.challengeToken ?? ""
  const setupRequired = Boolean(state.setupRequired)
  const [setupSecret, setSetupSecret] = useState("")
  const [setupUri, setSetupUri] = useState("")
  const [code, setCode] = useState("")
  const [loading, setLoading] = useState(false)
  const [setupLoading, setSetupLoading] = useState(setupRequired)
  const [error, setError] = useState("")

  const navigateAfterLogin = (user: User) => {
    if (user.role === "council_admin" && user.councils && user.councils.length > 0) {
      navigate(`/council/${user.councils[0]?.id}`)
    } else {
      navigate("/")
    }
  }

  useEffect(() => {
    if (!challengeToken) {
      navigate("/login", { replace: true })
      return
    }

    if (!setupRequired) return

    let mounted = true
    setSetupLoading(true)
    authenticationApi
      .startTwoFactorSetup(challengeToken)
      .then((response) => {
        if (!mounted) return
        setSetupSecret(response.data.data?.secret ?? "")
        setSetupUri(response.data.data?.otpauthUri ?? "")
      })
      .catch((err: unknown) => {
        if (!mounted) return
        setError((err as Error).message || "Failed to start two-factor setup")
      })
      .finally(() => {
        if (mounted) setSetupLoading(false)
      })

    return () => {
      mounted = false
    }
  }, [challengeToken, navigate, setupRequired])

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault()
    setLoading(true)
    setError("")

    try {
      const request = { challengeToken, code }
      const response = setupRequired
        ? await authenticationApi.verifyTwoFactorSetup(request)
        : await authenticationApi.verifyTwoFactorLogin(request)
      const user = response.data.data?.user
      if (!user) {
        throw new Error("Two-factor verification failed")
      }
      const result = authStore.completeLogin(user)
      if (result.type === "authenticated") {
        navigateAfterLogin(result.user)
      }
    } catch (err: unknown) {
      setError((err as Error).message || "Two-factor verification failed")
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-gradient-to-br from-primary/10 via-background to-accent/5 p-4">
      <div className="w-full max-w-lg space-y-6">
        <div className="space-y-2 text-center">
          <div className="mx-auto flex size-12 items-center justify-center rounded-lg bg-primary text-primary-foreground">
            <Shield className="size-7" />
          </div>
          <h1 className="text-2xl font-bold">Two-Factor Authentication</h1>
          <p className="text-muted-foreground">
            {setupRequired
              ? "Set up two-factor authentication before continuing."
              : "Enter your authenticator code to continue."}
          </p>
        </div>

        <Card>
          <CardHeader>
            <CardTitle>{setupRequired ? "Set Up 2FA" : "Verify 2FA"}</CardTitle>
            <CardDescription>
              {state.email ? `Signed in as ${state.email}` : "Your password was accepted."}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-5">
              {error && (
                <Alert variant="destructive">
                  <AlertDescription>{error}</AlertDescription>
                </Alert>
              )}

              {setupRequired && (
                <div className="space-y-4 rounded-lg border p-4">
                  <div className="space-y-1">
                    <p className="font-medium">1. Add Water Bureau to your authenticator app</p>
                    <p className="text-sm text-muted-foreground">
                      Use Google Authenticator, Microsoft Authenticator, 1Password, or another TOTP
                      app.
                    </p>
                  </div>

                  {setupLoading ? (
                    <div className="flex justify-center py-8">
                      <LoadingSpinner />
                    </div>
                  ) : (
                    <>
                      {setupUri && (
                        <div className="flex justify-center rounded-lg bg-white p-4">
                          <QRCodeSVG value={setupUri} size={200} />
                        </div>
                      )}
                      {setupSecret && (
                        <div className="space-y-1">
                          <p className="text-xs font-medium">Manual setup key</p>
                          <p className="break-all rounded bg-muted p-2 font-mono text-xs">
                            {setupSecret}
                          </p>
                        </div>
                      )}
                    </>
                  )}
                </div>
              )}

              <div className="space-y-2">
                <Label htmlFor="two-factor-code">
                  {setupRequired ? "2. Enter the 6-digit code" : "Authenticator Code"}
                </Label>
                <Input
                  id="two-factor-code"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  placeholder="123456"
                  value={code}
                  onChange={(event) => setCode(event.target.value)}
                  required
                />
              </div>

              {setupRequired && (
                <Alert className="border-green-200 bg-green-50 text-green-800">
                  <CheckCircle className="size-4" />
                  <AlertDescription>
                    After this succeeds, 2FA will be enabled for future logins.
                  </AlertDescription>
                </Alert>
              )}

              <Button type="submit" className="w-full" disabled={loading || setupLoading}>
                {loading ? (
                  <span className="flex items-center gap-2">
                    <LoadingSpinner size="sm" />
                    Verifying...
                  </span>
                ) : (
                  "Continue"
                )}
              </Button>

              <Button
                type="button"
                variant="ghost"
                className="w-full"
                onClick={() => navigate("/login")}
              >
                Back to sign in
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  )
}
